import {
    Box,
    HStack,
    Select,
    Spinner,
    Text,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { OrganizationSportGameDurationDto } from '../../../models/organization/dto/organization-sport-game-duration.dto';
import { useGetOrganizationGameDurations } from '../../organization/hooks/useOrganizationGameDurations';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ErrorState from '../../../components/states/ErrorState';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import GameDurationEditor from './GameDurationEditor';

const SPORT_OPTIONS = [
  { value: '', label: translate('game_durations.all_sports') },
  { value: SportType.PADEL, label: 'Padel' },
  { value: SportType.TENNIS, label: 'Tennis' },
  { value: SportType.BASKETBALL, label: 'Basketball' },
  { value: SportType.VOLLEYBALL, label: 'Volleyball' },
  { value: SportType.BADMINTON, label: 'Badminton' },
  { value: SportType.FOOTBALL, label: 'Football' },
];

export default function GameDurationsList() {
  const { selectedOrganization } = useSelectedOrganization();
  const [selectedSport, setSelectedSport] = useState<SportType | ''>('');

  const { data: gameDurations, isInitialLoading, isError } = useGetOrganizationGameDurations(
    selectedOrganization?.id || '',
    selectedSport ? { sport: selectedSport as SportType } : undefined
  );

  /**
   * Which of the four states this read is in, taken from the query's OWN flags
   * (D62) rather than from the `!selectedOrganization` early return this file
   * used to carry.
   *
   * The store guard was CORRECT — it completely re-derived the hook's single
   * `enabled: !!organizationId` term, so the never-issued case reached a text
   * prompt and never the spinner below it. It was also the fragile shape D62
   * names: the gate lives in `useOrganizationGameDurations.ts` and the guard
   * lived here, so adding a second `enabled:` term (a sport, a club) would have
   * left this file spinning forever with nothing to catch it. Run 110's
   * `LeaderboardPage` is that exact accident already having happened elsewhere.
   *
   * The error arm moves with it, and that half IS a live behaviour change: it
   * keyed off the raw `error` before looking at the data, so a background
   * refetch that failed over durations already on screen replaced the whole
   * list with an error card. `classifyRead`'s `unreadable` requires `!data`
   * (R96-3).
   */
  const readState = classifyRead(isError, isInitialLoading, gameDurations);

  const groupedDurations = useMemo(() => {
    const groups = gameDurations?.reduce((acc, duration) => {
      if (!acc[duration.sport]) {
        acc[duration.sport] = [];
      }
      acc[duration.sport]!.push(duration);
      return acc;
    }, {} as Partial<Record<SportType, OrganizationSportGameDurationDto[]>>) || {};
    // Sort within each group so we don't re-sort in the render path
    for (const sport of Object.keys(groups) as SportType[]) {
      groups[sport]?.sort((a, b) => a.durationMinutes - b.durationMinutes);
    }
    return groups;
  }, [gameDurations]);

  const sortedSports = useMemo(() =>
    (Object.keys(groupedDurations) as SportType[])
      .filter(sport => groupedDurations[sport] && groupedDurations[sport]!.length > 0)
      .sort(),
    [groupedDurations]
  );

  const sectionColor = useColorModeValue('gray.700', 'gray.300');
  const sectionBorderColor = useColorModeValue('gray.200', 'gray.600');

  if (readState === 'never-issued') {
    return <ScopePrompt gate="organization" />;
  }

  if (readState === 'loading') {
    return (
      <Box display="flex" justifyContent="center" p={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (readState === 'unreadable') {
    return <ErrorState message={translate('game_durations.error_loading')} />;
  }

  return (
    <Box p={4}>
      <HStack mb={6} justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {translate('game_durations.title')}
        </Text>
        <Box minWidth="200px">
          <Select
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value as SportType | '')}
            placeholder={translate('game_durations.filter_by_sport')}
          >
            {SPORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Box>
      </HStack>

      {!gameDurations || gameDurations.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.500" fontSize="lg">
            {translate('game_durations.no_durations')}
          </Text>
        </Box>
      ) : (
        <VStack spacing={6} align="stretch">
          {sortedSports.map((sport) => (
            <Box key={sport}>
              <Text
                fontSize="md"
                fontWeight="semibold"
                mb={3}
                color={sectionColor}
                borderBottom="1px"
                borderColor={sectionBorderColor}
                pb={2}
              >
                {sport}
              </Text>
              <VStack spacing={3} align="stretch">
                {(groupedDurations[sport] || []).map((duration: OrganizationSportGameDurationDto) => (
                    <GameDurationEditor
                      key={duration.id}
                      gameDuration={duration}
                    />
                  ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
}